import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import intl from "components/i18n/ReactIntlWrapper";
import HRCalendarNavbar from "./HRCalendarNavbar";
import { useApi } from "../../../api/ApiProvider";
import Api from "../../../axiosApi/api";
import { useToastMessageQueue } from "../../../components/ToastMessages/ToastMessageProvider";
import GenericCalendar from "./GenericCalendar";
import Spinner from "../../Spinner";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { handleAPIError } from "../../../common/errorHandler";
import { WorkflowProvider } from "components/Workflow/WorkflowContext";
import { WORKFLOW_MODULES } from "common/constants";
import {
  Card,
  Typography,
  Box,
  Link as MuiLink,
} from "@mui/material";
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { useCalendarEvents, useCalendarEventsSearch } from 'store/registry/useCalendarEvents';
import { startOfMonth, endOfMonth } from 'date-fns';
import { getUserEntity } from 'common/UserEntityProvider';
import { useEmployeeTimeOffRegime, useTimeOffTypes } from 'store/registry/useTimeOffData';

export type CalendarInput = {
  idCountry: number | null;
  country: any;
  idTimeOffRequestType: null;
  idWorkflowState: number | null;
  queryTerm?: string | null;
  currentPage?: number;
  sortValue?: string | null;
  orderValue?: string | null;
};

export type SearchResult = {
  totalRecords: number | null;
  queryResult: any;
};

const HRCalendar = ({ HRView = false }) => {
  const [input, setInput] = useState<CalendarInput | null>({
    idCountry: null,
    country: { name: "" },
    idTimeOffRequestType: null,
    idWorkflowState: null,
    queryTerm: "",
    currentPage: 1,
    sortValue: "Name",
    orderValue: "asc",
  });

  // Date range state for calendar
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(() => {
    const now = new Date();
    return {
      start: startOfMonth(now),
      end: endOfMonth(now)
    };
  });

  const [nonWorkingDay, setNonWorkingDay] = useState(false);

  const [countries, setCountries] = useState(null);

  const [requestStatus, setRequestStatus] = useState(null);

  const [isCalendarEventDetailOpen, setIsCalendarEventDetailOpen] =
    useState(false);

  const closeCalendarEventDetail = () => setIsCalendarEventDetailOpen(false);

  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  const location = useLocation();

  const api: Api = useApi();

  const toast = useToastMessageQueue();

  let limit: number = 100;

  // Build filters for the search
  const buildFilters = (): Array<string> | undefined => {
    const filters: Array<string> = [];
    if (input?.idCountry) {
      filters.push(`country:${input.idCountry}`);
    }
    if (input?.idTimeOffRequestType) {
      filters.push(`type:${input.idTimeOffRequestType}`);
    }
    return filters.length > 0 ? filters : undefined;
  };

  // Use cached calendar events hooks
  const { data: eventsFromSearch, isLoading: isLoadingSearch, refetch: refetchSearch } = useCalendarEventsSearch(
    input?.queryTerm,
    input?.orderValue,
    buildFilters()
  );

  // Fetch events for the current date range
  const { data: eventsFromDateRange, isLoading: isLoadingDateRange, refetch: refetchDateRange } = useCalendarEvents(
    dateRange.start,
    dateRange.end,
    undefined, // eventType - fetch all types
    true // enabled
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [timeOffEvents2, setTimeOffEvents2] = useState<any>({
    totalRecords: null,
    queryResult: null,
  });

  const entityType = getUserEntity();

  // Use cached hooks for time off data (only for non-HR view)
  const { regimeId } = useEmployeeTimeOffRegime(
    entityType?.entityId || 0, 
    !HRView && !!entityType?.entityId
  );

  const { data: timeOffTypesData } = useTimeOffTypes(regimeId, HRView, HRView || !!regimeId);
  
  // Use timeOffTypesData for requestType
  const requestType = { queryResult: timeOffTypesData };

  // Process events from search
  const processedEventsFromSearch = eventsFromSearch.map(event => {
    // Ensure dates are Date objects
    const startDate = event.start instanceof Date ? event.start : new Date(event.start);
    const endDate = event.end instanceof Date ? event.end : new Date(event.end);
    
    if (event.eventType === "TimeOff") {
      return {
        ...event,
        start: startDate,
        end: endDate,
        title: `${event.timeOffRequestTypeName} - ${event.employeeName} ${event.employeeLastName}`,
      };
    } else if (event.eventType === "NonWorkingDay") {
      return {
        ...event,
        start: startDate,
        end: startDate, // Use start date for non-working days
      };
    }
    return {
      ...event,
      start: startDate,
      end: endDate
    };
  });

  // Process events from date range
  const processedEventsFromDateRange = eventsFromDateRange.map(event => {
    // Ensure dates are Date objects
    const startDate = event.start instanceof Date ? event.start : new Date(event.start);
    const endDate = event.end instanceof Date ? event.end : new Date(event.end);

    if (event.eventType === "TimeOff") {
      return {
        ...event,
        start: startDate,
        end: endDate,
        title: event.title || `${event.timeOffRequestTypeName} - ${event.employeeName} ${event.employeeLastName}`,
      };
    } else if (event.eventType === "NonWorkingDay") {
      return {
        ...event,
        start: startDate,
        end: startDate, // Use start date for non-working days
      };
    }
    return {
      ...event,
      start: startDate,
      end: endDate
    };
  });

  // Merge all events and remove duplicates based on id
  const allProcessedEvents = [...processedEventsFromSearch, ...processedEventsFromDateRange];
  const uniqueEvents = allProcessedEvents.reduce((acc, event) => {
    if (!acc.find(e => e.id === event.id && e.eventType === event.eventType)) {
      acc.push(event);
    }
    return acc;
  }, [] as any[]);

  // Filter events by selected country
  const filteredEvents = uniqueEvents.filter((event) => {
    // If no country is selected, show all events
    if (!input?.idCountry) {
      return true;
    }
    // For NonWorkingDay events, filter by country
    if (event.eventType === "NonWorkingDay") {
      return event.idCountry === input.idCountry;
    }
    // For TimeOff events, we might not have country info directly,
    // so we keep them if no country filter or if they match
    return !event.idCountry || event.idCountry === input.idCountry;
  });

  const timeOffEventsFromSearch = filteredEvents.filter(
    (event) => event.eventType === "TimeOff"
  );

  const nonWorkingDayEventsFromSearch = filteredEvents.filter(
    (event) => event.eventType === "NonWorkingDay"
  );

  const fetchRequests = async (
    term?: string,
    pageNum?: number,
    pageSize?: number,
    sort?: string,
    order?: string,
    f?: Array<string>
  ) => {
    if (input.idTimeOffRequestType) {
      f = [`type:${input.idTimeOffRequestType}`];
    }
    let endpoint;
    if (location.pathname === "/CalendarHR") {
      endpoint = "apiVversionTimeOffSearchGet";
    } else if (location.pathname === "/Calendar") {
      endpoint = "apiVversionTimeOffSearchMineGet";
    }
    const response = await api.timeoffApi[endpoint](
      "1",
      input.queryTerm,
      input.currentPage,
      limit,
      input.sortValue,
      input.orderValue,
      f,
      {}
    )
      .then((response) => {
        if (response.data.data) {
          const extraFilter = response.data?.data?.queryResult.filter(
            (timeoff) =>
              (input.idWorkflowState
                ? timeoff.idWorkflowState === input.idWorkflowState
                : timeoff) &&
              (input.idTimeOffRequestType
                ? timeoff.idTimeOffRequestType === input.idTimeOffRequestType
                : timeoff)
          );
          const renamedEvents = extraFilter.map((event) => ({
            ...event,
            start: new Date(event.from),
            end: new Date(event.to),
            from: undefined,
            to: undefined,
          }));
          const renamedTimeOffEvents = renamedEvents.map((event) => {
            const newEndDate = new Date(event.end);
            newEndDate.setDate(newEndDate.getDate() + 1);
            return {
              ...event,
              end: newEndDate,
              title: `${event.timeOffRequestTypeName} - ${event.employeeName} ${event.employeeLastName}`,
            };
          });
          setTimeOffEvents2({
            totalRecords: renamedTimeOffEvents.length,
            queryResult: renamedTimeOffEvents,
          });
        } else {
          setTimeOffEvents2({
            totalRecords: null,
            queryResult: null,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      });
  };

  // fetchRequestType is now handled by useTimeOffTypes hook
  const fetchCountries = async () => {
    const response = await api.addressApi
      .apiVversionAddressCountryAllGet("1", {})
      .then((response) => {
        if (response.data) {
          setCountries(response.data.data);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      });
  };
  useEffect(() => {
    if (nonWorkingDay) {
      refetchSearch();
    }
    // Refetch events for the current month when dateRange changes
    refetchDateRange();
  }, [dateRange]);

  const handleMonthChange = (start: Date, end: Date) => {
    setDateRange({ start, end });
  };

  const updateEvents = async () => {
    setLoading(true);
    try {
      await refetchSearch();
      await refetchDateRange();
      if (
        location.pathname == "/Calendar" ||
        location.pathname == "/CalendarHR"
      ) {
        await fetchRequests();
      }
    } catch (error) {
      console.error("Error updating events:", error);
    } finally {
      setLoading(false);
    }
  };

  // Data fetching is now handled by useEmployeeTimeOffRegime and useTimeOffTypes hooks
  // which automatically refetch when entityId, regimeId, or HRView changes

  useEffect(() => {
    if (
      location.pathname == "/Calendar" ||
      location.pathname == "/CalendarHR"
    ) {
      fetchRequests();
    }
    if (location.pathname == "/Calendar/Holidays") {
      fetchCountries();
      setNonWorkingDay(true);
    }
  }, [location.pathname]);

  const handleCountryChange = (e) => {
    if (e !== null) {
      const inputVal = {
        ...input,
        idCountry: e.id,
        country: { name: intl.get(e.labelKey), labelKey: e.labelKey },
      };
      setInput({ ...inputVal });
    } else {
      const inputVal = {
        ...input,
        idCountry: null,
        country: { name: "" },
      };
      setInput({ ...inputVal });
    }
  };

  const onSearch = (queryString, idStatus, idRequest) => {
    setInput({
      ...input,
      queryTerm: queryString,
      idTimeOffRequestType: idRequest,
      idWorkflowState: idStatus,
      currentPage: 1,
    });
  };

  const handleMyPTO = () => {
    navigate("/timeOff");
  };

  const handleManagePTO = () => {
    navigate("/timeOffHR");
  };

  return (
    <Card sx={{ m: 2 }}>
    <Box sx={{ p: 3 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography color="primary" variant="h2" sx={{ mb: 2 }}>
          {nonWorkingDay
            ? intl.get("hRCalendar.header.holidays")
            : HRView
            ? intl.get("hRCalendar.header")
            : intl.get("timeOff.header")}
        </Typography>
        {!nonWorkingDay && (
          <MuiLink
            component="button"
            variant="body2"
            color="secondary"
            onClick={HRView ? handleManagePTO : handleMyPTO}
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "1rem",
            }}
          >
            {intl.get("hRCalendar.list")}
            <CalendarTodayOutlinedIcon
              style={{ marginLeft: 4, marginBottom: 2 }}
            />
          </MuiLink>
        )}
      </Box>
      <Box sx={{ px: 3 }}>

        <WorkflowProvider moduleId={WORKFLOW_MODULES.TIME_OFF}>
          <HRCalendarNavbar
            handleCountryChange={handleCountryChange}
            selectedCountry={input?.country.labelKey}
            countries={countries}
            input={input}
            nonWorkingDay={nonWorkingDay}
            requestType={requestType?.queryResult}
            onSearch={onSearch}
            HRView={HRView}
          />
        </WorkflowProvider>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Spinner />
          </Box>
        ) : (
          <GenericCalendar
            events={
              nonWorkingDay
                ? [...(nonWorkingDayEventsFromSearch || [])]
                : [
                    ...(nonWorkingDayEventsFromSearch || []),
                    ...(timeOffEvents2?.queryResult || []),
                  ]
            }
            nonWorkingDay={nonWorkingDay}
            updateEvents={updateEvents}
            onMonthChange={handleMonthChange}
            isLoading={isLoadingSearch || isLoadingDateRange}
          />
        )}
      </Box>
    </Box>
    </Card>
  );
};

export default HRCalendar;
